import React from 'react';
import { Button, ButtonProps, useTheme } from '@mui/material';

interface SecondaryButtonProps extends Omit<ButtonProps, 'variant' | 'color'> {
  /**
   * Variante do botão
   * @default 'contained'
   */
  variant?: 'contained' | 'outlined' | 'text';
  /**
   * Cor do botão
   * @default 'secondary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /**
   * Tamanho do botão
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Se deve ocupar toda a largura disponível
   * @default false
   */
  fullWidth?: boolean;
}

/**
 * Componente de botão secundário para ações de menor prioridade
 * Geralmente usado para ações de cancelamento, voltar, ou ações auxiliares
 */
const SecondaryButton: React.FC<SecondaryButtonProps> = ({
  children,
  variant = 'contained',
  color = 'secondary',
  size = 'medium',
  fullWidth = false,
  sx,
  ...props
}) => {
  const theme = useTheme();

  const getButtonStyles = () => {
    const baseStyles = {
      borderRadius: '6px', // Ligeiramente menor que o PrimaryButton
      textTransform: 'none' as const,
      fontWeight: 400, // Menos pesado que o PrimaryButton
      transition: 'all 0.2s ease', // Transição mais rápida
      minHeight: size === 'small' ? 30 : size === 'large' ? 44 : 36,
      fontSize: size === 'small' ? '0.8rem' : size === 'large' ? '1rem' : '0.9rem',
    };

    if (variant === 'contained') {
      if (color === 'secondary') {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            backgroundColor: theme.palette.grey[200],
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
          },
          '&:active': {
            backgroundColor: theme.palette.grey[300],
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
          },
          '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
            boxShadow: 'none',
          },
        };
      } else if (color === 'primary') {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.grey[600],
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.grey[700],
          },
          '&:active': {
            backgroundColor: theme.palette.grey[800],
          },
          '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
          },
        };
      } else if (color === 'error') {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.error.main,
          },
          '&:active': {
            backgroundColor: theme.palette.error.dark,
          },
          '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
          },
        };
      }
    } else if (variant === 'outlined') {
      const borderColor = color === 'primary' ? theme.palette.grey[400] : 
                          color === 'secondary' ? theme.palette.grey[400] : 
                          color === 'error' ? theme.palette.error.main : theme.palette.grey[400];
      const textColor = color === 'primary' ? theme.palette.text.primary : 
                        color === 'secondary' ? theme.palette.text.secondary : 
                        color === 'error' ? theme.palette.error.main : theme.palette.text.primary;
      
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: textColor,
        border: `1px solid ${borderColor}`,
        '&:hover': {
          backgroundColor: color === 'primary' 
            ? `${theme.palette.grey[500]}08` 
            : color === 'secondary' 
            ? `${theme.palette.grey[400]}12`
            : `${theme.palette.error.main}08`,
          borderColor: color === 'primary' ? theme.palette.grey[600] : 
                       color === 'secondary' ? theme.palette.grey[500] : 
                       theme.palette.error.dark,
        },
        '&:active': {
          backgroundColor: color === 'primary' 
            ? `${theme.palette.grey[500]}15` 
            : color === 'secondary' 
            ? `${theme.palette.grey[400]}20`
            : `${theme.palette.error.main}15`,
        },
        '&:disabled': {
          borderColor: theme.palette.action.disabled,
          color: theme.palette.action.disabled,
        },
      };
    } else if (variant === 'text') {
      const textColor = color === 'primary' ? theme.palette.text.primary : 
                        color === 'secondary' ? theme.palette.text.secondary : 
                        color === 'error' ? theme.palette.error.main : theme.palette.text.secondary;
      
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: textColor,
        padding: '4px 12px', // Padding menor que o PrimaryButton
        '&:hover': {
          backgroundColor: color === 'primary' 
            ? `${theme.palette.grey[500]}08` 
            : color === 'secondary' 
            ? `${theme.palette.grey[400]}08`
            : `${theme.palette.error.main}08`,
        },
        '&:disabled': {
          color: theme.palette.action.disabled,
        },
      };
    }

    return baseStyles;
  };

  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      sx={{
        ...getButtonStyles(),
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export default SecondaryButton;
